import Popover from "@mui/material/Popover";
import classNames from "classnames";
import { MouseEventHandler, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useMenu } from "components/ActionMenu/useMenu";
import { EditIcon } from "components/Icons";
import { WithSearchVirtualized } from "components/OutlinedSelect/OutlinedSelect";

import { SelectItems } from "./SelectItems";
import { getBulkItemTitle } from "./utils";

import styles from "./styles.module.scss";

type ResourceObject = string | { name: string; catalogName: string };

type Props = {
  objects: ResourceObject[];
  items: any[];
  prefixItems?: React.ReactElement[];
  resource: string;
  index: number;
  searchPlaceholder: string;
  renderItem: (item: any) => React.ReactElement;
  renderCatalogItem?: (item: string, catalogName: string) => React.ReactElement;
  searchKey?: string;
  expanded?: boolean;
  handleMore?: MouseEventHandler;
};

const MIN_WIDTH = 240;
const MAX_TEXT_LENGTH = 40;

export const GroupInner = (props: Props) => {
  const {
    objects,
    prefixItems,
    items,
    resource,
    index,
    searchPlaceholder,
    searchKey,
    expanded,
    handleMore,
  } = props;
  const { menuElement, openMenu, closeMenu } = useMenu();
  const { t } = useTranslation();
  const menuMinWidth = useRef<number>(0);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const editRef = useRef<HTMLDivElement | null>(null);

  const setInitialWidth = () => {
    const { width = 0 } = menuRef.current?.getBoundingClientRect() || {};
    menuMinWidth.current = width;
  };

  const handleOpen = (event: React.MouseEvent) => {
    setInitialWidth();
    openMenu(event);
  };

  const renderCatalogItem = (name: React.ReactNode, catalogName: string) => {
    if (catalogName) {
      const resourceName = catalogName + "/" + name;
      return (
        <span
          className={styles.resourceName}
          title={resourceName}
        >
          {resourceName}
        </span>
      );
    }
    return <span className={styles.resourceName}>{name}</span>;
  };

  const renderSpecialValue = (name: string) => {
    const bulkItemTitle = getBulkItemTitle(t, name);
    const resourceName = bulkItemTitle || name;
    return resourceName;
  };

  const getListItem = (value: string, object: ResourceObject) => {
    if (typeof object === "string") {
      return value;
    }
    const renderItem = props.renderCatalogItem || renderCatalogItem;
    return renderItem(value, object.catalogName);
  };

  const renderObjects = () => {
    const renderObject = (object: ResourceObject) => {
      const name = typeof object === "string" ? object : object.name;

      const value = renderSpecialValue(name);

      const key =
        typeof object === "string"
          ? object
          : object.name + "/" + object.catalogName;

      const listItem = getListItem(value, object);

      return (
        <div
          className={styles.object}
          key={key}
          title={key}
        >
          {listItem}
        </div>
      );
    };

    const { count } = objects.reduce(
      (acc, object, index) => {
        const length =
          typeof object === "string"
            ? object.length
            : (object.catalogName?.length ?? 0) + object.name.length + 1;
        // Ensure at least one item is displayed, even if it exceeds MAX_TEXT_LENGTH
        if (index === 0 || acc.textLength + length <= MAX_TEXT_LENGTH) {
          acc.textLength += length;
          acc.count += 1;
        }
        return acc;
      },
      {
        textLength: 0,
        count: 0,
      }
    );

    if (!expanded && objects.length > count) {
      const moreCount = objects.length - count;
      const visibleItems = objects.slice(0, count);
      return (
        <div className={styles.more}>
          <div className={styles.more__items}>
            {visibleItems
              .map(object => {
                if (typeof object === "string") {
                  return renderSpecialValue(object);
                }
                const name = renderSpecialValue(object.name);
                return props.renderCatalogItem
                  ? name
                  : object.catalogName + "/" + name;
              })
              .join(", ")}
          </div>
          <div className={styles.more__action}>
            <a onClick={handleMore}>{t("roles_wizard.more", { moreCount })}</a>
          </div>
        </div>
      );
    }
    if (expanded && objects.length > count) {
      return (
        <div className={styles.objects}>
          {objects.map(renderObject)}
          <div className={styles.less__action}>
            <a onClick={handleMore}>{t("roles_wizard.hide")}</a>
          </div>
        </div>
      );
    }
    return (
      <div
        className={classNames(styles.objects, styles.objects__inline, {
          [styles.comma]: objects.length >= 2,
        })}
      >
        {objects.length ? (
          objects.map(renderObject)
        ) : (
          <div
            className={classNames(
              styles.placeholder,
              styles.placeholder__objects
            )}
            onClick={handleOpen}
          >
            Select objects
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={styles.group__inner}
        ref={menuRef}
      >
        {renderObjects()}
        <div
          className={classNames(styles.edit, {
            [styles.edit__active]: !!menuElement,
          })}
          onClick={handleOpen}
          data-testid={`roles-wizard-edit-objects-${resource}-${index}`}
          ref={editRef}
        >
          <EditIcon />
        </div>
      </div>
      <Popover
        open={!!menuElement}
        anchorEl={editRef.current}
        onClose={closeMenu}
        marginThreshold={8}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: {
              minWidth: menuMinWidth.current,
              width: Math.max(menuMinWidth.current, MIN_WIDTH),
              marginTop: 2,
            },
          },
        }}
        data-testid={`edit-objects-${resource}`}
      >
        <WithSearchVirtualized
          renderItem={props.renderItem}
          items={items}
          searchOptions={{
            searchPlaceholder,
            searchKey,
          }}
        >
          <SelectItems prefixItems={prefixItems} />
        </WithSearchVirtualized>
      </Popover>
    </>
  );
};
